import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpStatusCode,
} from '@angular/common/http';
import {inject} from '@angular/core';
import {
  EnvironmentHelper,
  TranslateHelper,
  UserAgentHelper,
} from '@binotel-frontend/bnt-common-components';
import {
  EApiKnownErrors,
  IApi,
  IApiError,
  IApiErrors,
  IApiMethod,
  IHttpBody,
  IHttpOptions,
} from '@entities/api';
import {ESnackbarType} from '@entities/ui/snackbar';
import {environment} from '@envs/environment';
import {IS_BROWSER} from '@shared/constants/is-browser.token';
import {ErrorsHelper, SnackbarHelper} from '@shared/helper';
import {catchError, map, Observable, tap} from 'rxjs';

const UNAUTHORIZED_STATUS = 401;
const UPGRADE_REQUIRED_STATUS = 426;
const TOO_MANY_REQUESTS_STATUS = 429;

const SERVER_ERROR_STATUSES: number[] = [
  HttpStatusCode.InternalServerError,
  HttpStatusCode.ServiceUnavailable,
];

const NOT_CRITICAL_STATUSES: number[] = [
  HttpStatusCode.Unauthorized,
  HttpStatusCode.Forbidden,
  HttpStatusCode.NotFound,
  HttpStatusCode.UnprocessableEntity,
  HttpStatusCode.ServiceUnavailable,
];

const SERVER_PLATFORM = 'Server';

export class OdApiErrorResponse extends Error {
  public notCritical? = false;
  public error: IApiErrors;
  public connectionError = false;
  public status: number;
  public url: string;
  public data: null;

  constructor(error: Error, message?: string) {
    super(message ?? error.message);
    this.handleConnectionErrors(error as HttpErrorResponse);
    this.reassignOriginErrorProps(error as HttpErrorResponse);

    if (message) {
      this.message = message;
    }
  }

  private reassignOriginErrorProps(error: HttpErrorResponse): void {
    const keys = ['error', 'status', 'stack', 'url', 'message'];
    for (const key of keys) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this[key] = error[key];
    }
    this.error =
      (error.error as IApiError)?.error ??
      (error as unknown as IApi<unknown>).error;

    if (this.error && !this.isApiErrors(this.error)) {
      this.error = {
        [EApiKnownErrors.GeneralError]: ['common.error-occurred'],
      };
    }

    // TODO: 'generalErrorData' is extended data for 'generalError'.

    delete this.error?.generalErrorData;

    if (error.status === TOO_MANY_REQUESTS_STATUS) {
      this.error = {
        [EApiKnownErrors.GeneralError]: ['common.too-many-requests'],
      };
      this.notCritical = true;
    }

    if (error.status === UNAUTHORIZED_STATUS) {
      this.error = {
        [EApiKnownErrors.GeneralError]: ['common.session-expired'],
      };
    }

    if (SERVER_ERROR_STATUSES.includes(error.status)) {
      this.error = {
        [EApiKnownErrors.GeneralError]: ['common.server-error'],
      };
    }

    if (this.connectionError || NOT_CRITICAL_STATUSES.includes(error.status)) {
      this.notCritical = true;
    }

    if (ErrorsHelper.isUserInputError(this.error)) {
      this.notCritical = true;
    }

    if (this.error) {
      this.error = ErrorsHelper.checkTranslationLabels(this.error);
    }

    if (error.status === UPGRADE_REQUIRED_STATUS) {
      this.notCritical = true;
    }
  }

  private handleConnectionErrors(error: HttpErrorResponse): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (error.status === 0 && !error.error?.errors) {
      this.connectionError = true;
      SnackbarHelper.open(
        TranslateHelper.translate('common.no-internet-connection'),
        ESnackbarType.Error
      );
    }
  }

  private isApiErrors(error: unknown): error is IApiErrors {
    return (
      typeof error === 'object' &&
      !Array.isArray(error) &&
      !(error instanceof Error)
    );
  }
}

export class ApiService {
  private readonly isBrowser = inject(IS_BROWSER);

  private readonly defaultRequestOptions: IHttpOptions = {
    withCredentials: true,
    headers: new HttpHeaders()
      .set('X-Requested-With', 'XMLHttpRequest')
      .set('ngsw-bypass', '*')
      .set('X-Binotel-User-Agent', this.buildUserAgent()),
  };

  protected readonly apiUrl = `${environment.apiDomain}/${environment.apiVersion}`;
  protected readonly http = inject(HttpClient);

  protected put<T>(
    url: string,
    body: IHttpBody,
    options?: IHttpOptions
  ): Observable<T> {
    return this.request('put', url, options, body);
  }

  protected post<T>(
    url: string,
    body: IHttpBody,
    options?: IHttpOptions
  ): Observable<T> {
    return this.request('post', url, options, body);
  }

  protected patch<T>(
    url: string,
    body: IHttpBody,
    options?: IHttpOptions
  ): Observable<T> {
    return this.request('patch', url, options, body);
  }

  protected delete<T>(
    url: string,
    body: IHttpBody,
    options?: IHttpOptions
  ): Observable<T> {
    return this.request('delete', url, options, body);
  }

  protected get<T>(url: string, options?: IHttpOptions): Observable<T> {
    return this.request('get', url, options);
  }

  protected request<T>(
    method: IApiMethod,
    url: string,
    options: IHttpOptions,
    body?: IHttpBody
  ): Observable<T> {
    return this.http
      .request<T>(
        method,
        url,
        this.getPostOptions(
          method,
          this.normaliseQueryParams(
            body instanceof FormData ? body : {...body}
          ),
          options
        )
      )
      .pipe(
        tap((response) => {
          if (
            !(response as IApi<unknown>)?.data &&
            (response as IApi<unknown>)?.error
          ) {
            const error = new OdApiErrorResponse(
              response as HttpErrorResponse,
              'HTTP 200: Api Service Warning: no data'
            );
            error.notCritical = true;
            throw error;
          }
        }),
        map((response) => {
          const apiResponse: IApi<{success: boolean}> = response as IApi<{
            success: boolean;
          }>;
          if (
            apiResponse?.data?.success === true &&
            Object.keys(apiResponse.data).length === 1
          ) {
            apiResponse.data = null;
            return apiResponse as T;
          }
          return response;
        }),
        catchError((e: OdApiErrorResponse) => {
          if (e instanceof HttpErrorResponse) {
            const error = new OdApiErrorResponse(e);

            if (e.status === UPGRADE_REQUIRED_STATUS) {
              error.notCritical = true;
            }
            throw error;
          }

          throw e;
        })
      );
  }

  private getPostOptions(
    method: IApiMethod,
    body: IHttpBody,
    options?: IHttpOptions
  ): IHttpOptions {
    if (method === 'get') {
      return {
        ...this.defaultRequestOptions,
        withCredentials: false,
        ...options,
      };
    }

    if (method === 'post') {
      return {...this.defaultRequestOptions, ...options, body};
    }

    return {
      ...this.defaultRequestOptions,
      ...options,
      body,
      params: {
        ...options?.params,

        _method: method,
      },
    };
  }

  private buildUserAgent(): string {
    if (this.isBrowser) {
      return UserAgentHelper.getBntUserAgent(environment);
    }

    const project = environment.projectName?.replace(/\s+/g, '') || '';
    const env = EnvironmentHelper.getEnvByApiDomain(environment.apiDomain);

    return `${project} ${env} ${SERVER_PLATFORM} ${environment.releaseVersion}`;
  }

  private normaliseQueryParams(data: IHttpBody): IHttpBody {
    Object.keys(data).forEach((key) => {
      const record = data as Record<string, unknown>;

      if (record[key] === null || typeof record[key] === 'undefined') {
        delete record[key];
      }

      if (key.includes('[]') && typeof record[key] === 'object') {
        (record[key] as unknown[]).forEach((value, index) => {
          record[key.replace('[]', `[${index}]`)] = value;
        });
        delete record[key];
      }
    });

    return data;
  }
}
